import { AssetManager } from 'components/sharpr/assetManager/AssetManager';
import { ApiResourcify } from 'components/sharpr/api/ApiResourcify';
import { ensure } from 'components/sharpr/ensure/ensure';
import _ from 'lodash';

export function IntegrationBoxRoute($stateProvider) {
	'ngInject';
	$stateProvider.state('hub.loggedin.IntegrationBox', {
		url: '/admin/integrations/box',
		controllerAs: 'vm',
		controller: IntegrationBoxCtrl,
		permission: ['manage_box'],
		templateUrl: '/ng/hub/Integration/Box/IntegrationBoxCtrl.html?' + window.BUILD,
		resolve: {
			style: () => AssetManager.css('/ng/hub/Integration/Box/IntegrationBoxCtrl.css'),
			$title: () => 'Box Integration',
		},
	});
}

export class IntegrationBoxCtrl {
	constructor(Location, Api, Cfg, modal, Identity, $state, $document, $scope, $timeout) {
		'ngInject';
		$document.on('click', () => {
			this.hideStatusBoxes();
			this.$scope.$apply();
		});

		this.Location = Location;
		this.Api = Api;
		this.Cfg = Cfg;
		this.modal = modal;
		this.Identity = Identity;
		this.$state = $state;
		this.$scope = $scope;
		this.$timeout = $timeout;
		this.boxStateTokenKey = 'box_state_token';
		this.boxStateClientKey = 'box_state_client';
		this.defStatus = '==========';
		this.error = '';
		this.info = '';

		this.calculateStart();
	}

	/**
	 * Try-catch handler for calculating starting position
	 */
	async calculateStart() {
		this.setInitialState();
		try {
			await this.tryCalculateStart();
		} catch (err) {
			this.reportStatus('error', err);
		}
		this.reportStatus('info', this.defStatus);
		this.working = false;
	}

	/**
	 * Figure out path to send the user on
	 * 1. if they have already authorized Sharpr to access Box, show Box folders
	 * 2. if they are back here after the OAuth redirect from Box, verify state token
	 * 3. if the state token is good, get bearer token
	 * @throws {String} Will throw an error if 1) a hub change is detected or 2) the state token is invalid
	 */
	async tryCalculateStart() {
		if (this.hasChangedHubs()) {
			window.localStorage.removeItem(this.boxStateClientKey);
			this.refresh();
			throw 'Hub mismatch. Refreshing.';
		}

		const hasValidToken = await this.checkToken();

		if (hasValidToken) {
			await this.initDetails();
		} else {
			if (!this.searchParams.code) {
				// this is the initial load of this page. Create the URL that sends them to step 1 of Box OAuth process.
				this.OAuthUrl = this.constructOAuthUrl();
			} else if (!this.verifyStateToken(this.searchParams.state)) {
				// This is after they have come back from authorizing Sharpr to use their account.
				/// The state token we sent doesn't match the one they have now, so something weird or bad happened.
				throw 'Failed to connect to Box.';
			} else {
				const resp = await this.getBearerToken();
				if (resp !== false) {
					await this.initDetails();
				}
			}
		}
	}

	/**
	 * Sets the initial variable values for times when a full "refresh" needs to happen
	 */
	setInitialState() {
		this.searchParams = this.Location.getAllSearchParams();
		this.setInitialStateFoldersAndHooks();
		this.shouldShowStatusInfo = false;
		this.shouldShowStatusError = false;
		this.shouldShowStatusMismatch = false;
		this.shouldShowRefresh = false;
		this.shouldFlashInfo = false;
		this.shouldFlashError = false;
		this.shouldFlashMismatch = false;
		this.isDullStatusInfo = true;
		this.isDullStatusError = true;
		this.isDullStatusMismatch = true;
		this.isSaving = false;
		this.refreshing = false;
		this.pmClearFlashInfo = null;
		this.pmClearFlashError = null;
		this.pmClearFlashMismatch = null;
		this.working = true;
	}

	/**
	 * Detects hub changes. Page needs to pull fresh data for the new hub.
	 * @returns {Boolean}
	 */
	hasChangedHubs() {
		const wasHub = window.localStorage.getItem(this.boxStateClientKey);
		if (!wasHub) {
			return false;
		}
		if (wasHub != this.Identity.currentClient().id) {
			return true;
		}
		return false;
	}

	/**
	 * Updates one of the pieces of "status" display; eg. info, error, warning.
	 * Statuses are used to give feedback to the user about activity, errors, etc.
	 * @param {String} name - The name/type of the status being reported
	 * @param val - The status value being reported/set
	 */
	reportStatus(name, val) {
		switch (typeof val) {
			case 'boolean':
				this[name] = val;
				break;
			case 'object':
				val = val.toString();
			default:
				const valLine = val + '<br>';
				// prevents duplicates
				if (this[name] != valLine) {
					this[name] = valLine + this[name];
				}
		}
		const capName = _.capitalize(name);
		const dullProp = `isDullStatus${capName}`;
		if (!val || val == this.defStatus) {
			this[dullProp] = true;
		} else {
			this[dullProp] = false;
			this.flashStatusIcon(capName);
			if (name == 'error') {
				this.shouldShowRefresh = true;
			}
		}
	}

	/**
	 * Causes a status icon to flash
	 * @param {String} name - The name/type of the status being flashed
	 */
	flashStatusIcon(name) {
		const propName = `shouldFlash${name}`;
		const pmName = `pmClearFlash${name}`;
		this[propName] = true;
		if (this[pmName]) {
			this.$timeout.cancel(this[pmName]);
		}
		this[pmName] = this.$timeout(() => {
			this[propName] = false;
		}, 500);
	}

	/**
	 * Checks whether or not we have a valid oAuth token for this session
	 * @returns {Object}
	 * @throws {String} Will throw an error if we get a bad response back from the api
	 */
	async checkToken() {
		this.reportStatus('info', 'Checking auth details');
		const tmp = await this.Api.request('get', '/box/has_valid_token');
		if (this.hasBadResponse(tmp)) {
			throw 'Unable to pull authentication info';
		}

		return tmp.data;
	}

	/**
	 * Checks for bad http-response status
	 * @param {Object} resp - The http-response object
	 * @returns {Boolean}
	 */
	hasBadResponse(resp) {
		return !resp.status || typeof resp.data == 'undefined' || resp.status < 200 || resp.status > 299;
	}

	/**
	 * Initializes the page once we have a valid oAuth token
	 */
	async initDetails() {
		window.localStorage.setItem(this.boxStateClientKey, this.Identity.currentClient().id);
		await this.pullFoldersAndHooks();
	}

	/**
	 * Pulls folder and webhook data from Box
	 */
	async pullFoldersAndHooks() {
		this.setInitialStateFoldersAndHooks();

		let [tmp1, tmp2, folders] = await Promise.all([
			this.pullHooks('our', '/box/webhooks'),
			this.pullHooks('Box', '/box/their_webhooks'),
			this.getBoxFolders(0),
		]);

		this.ourSavedWebhooks = ensure.array(tmp1);
		if (!tmp2.this_hub) {
			tmp2.this_hub = [];
		}
		if (!tmp2.other_hub) {
			tmp2.other_hub = [];
		}
		this.theirSavedWebhooks = tmp2;

		this.hasSavedWebhooksUs = this.ourSavedWebhooks.length ? true : false;
		this.hasSavedWebhooksThem = this.theirSavedWebhooks.this_hub.length ? true : false;

		if (folders.length) {
			await this.arrangeFolders(folders);
			await this.showHiddenHookedFolders();
		}

		this.setSavedWebhooksMismatchDetails();
	}

	/**
	 * Pull hook data from api
	 * @param {String} type - The type of pull that we're doing
	 * @param {String} endpoint - The api endpoint suffix that we'll pull from
	 * @throws {String} Will throw an error if we get a bad response back from the api
	 */
	async pullHooks(type, endpoint) {
		let tmp = null;
		this.reportStatus('info', `Pulling ${type} folder connections`);
		tmp = await this.Api.request('get', endpoint);
		if (this.hasBadResponse(tmp)) {
			throw `Unable to pull ${type} folder connections`;
		}
		return tmp.data;
	}

	/**
	 * Sets the folder-and-hook initial variable values for times when a pull needs to happen
	 */
	setInitialStateFoldersAndHooks() {
		this.arrangedFolders = [];
		this.ourSavedWebhooks = [];
		this.theirSavedWebhooks = [];
		this.reportStatus('info', this.defStatus);
		this.reportStatus('error', this.defStatus);
		this.mismatch = false;
		this.hasMismatchLength = false;
		this.hasSavedWebhooksUs = false;
		this.hasSavedWebhooksThem = false;
	}

	/**
	 * Sets mismatch status-box details
	 */
	setSavedWebhooksMismatchDetails() {
		if (!this.hasSavedWebhooksUs && !this.hasSavedWebhooksThem) {
			return;
		}

		if (this.ourSavedWebhooks.length != this.theirSavedWebhooks.this_hub.length) {
			this.reportStatus('mismatch', true);
			this.hasMismatchLength = true;
			return;
		}
	}

	/**
	 * Check if the state token from Box matches the token we sent to Box.
	 * When we send a user to the Box OAuth authorization page, we include a state token in the URL. When Box sends the user
	 * back to Sharpr, it includes a state token in the URL. Something is wrong if those two don't match.
	 * @param tokenFromBox String  The token Box sent in the URL
	 * @returns {Boolean}
	 */
	verifyStateToken(tokenFromBox) {
		const originalToken = window.localStorage.getItem(this.boxStateTokenKey);

		return originalToken === tokenFromBox;
	}

	/**
	 * Creates a URL with all the components needed to start the OAuth process with Box
	 * @see https://developer.box.com/reference#authorize
	 * @returns {String}
	 */
	constructOAuthUrl() {
		// this must match what is set in the Box developer console (https://sharpr.app.box.com/developers/console/app/639069/configuration)
		const redirectUri = encodeURIComponent(window.BASE_URL + '/hub#/admin/integrations/box');
		// client id found here: https://sharpr.app.box.com/developers/console/app/639069/configuration
		const boxClientId = this.Cfg.read('Box.client_id');
		const stateToken = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
		const OAuthUrl = `https://account.box.com/api/oauth2/authorize?response_type=code&client_id=${boxClientId}&redirect_uri=${redirectUri}&state=${stateToken}`;
		window.localStorage.setItem(this.boxStateTokenKey, stateToken);

		return OAuthUrl;
	}

	/**
	 * Starts the process of exchanging a temporary OAuth code for a bearer token
	 * @returns {Promise}
	 * @throws {String} Will throw an error if we get a bad response back from the api
	 */
	async getBearerToken() {
		this.reportStatus('info', 'Authenticating with Box');
		const resp = await this.Api.request('get', `/v2/oauth_credentials/request_box_token?code=${this.searchParams.code}`);
		window.localStorage.removeItem(this.boxStateTokenKey);
		if (this.hasBadResponse(resp)) {
			let msg = 'Unable to authenticate with Box.';
			if (resp.data && resp.data.error_description) {
				msg += ` ${resp.data.error_description}.`;
			}
			throw msg;
		}
		return resp.data;
	}

	/**
	 * Adds properties to folder objects that are used for displaying and other logic. And inserts the folders sent in
	 * the specified location in the tree.
	 * @param foldersToAdd Array  An array of folder objects from the Box API
	 * @param insertAt Bool|Int  The index in `this.arrangedFolders` of the parent to these folders
	 * @param level Int  The level the `folders` should belong to. E.g., root folders are level 0, their subfolders are 1, etc.
	 * @param open Bool  If the folder should start as open
	 * @param checked Bool  If the folder should start as checked
	 */
	arrangeFolders(foldersToAdd, insertAt = false, level = 0, open = false, checked = false) {
		let folders = [];
		foldersToAdd.forEach(folder => {
			const chk = checked || (this.ourSavedWebhooks.filter(hook => hook.target_id == folder.id).length ? true : false);
			const otherHub = this.theirSavedWebhooks.other_hub.filter(hook => hook.target_id == folder.id);
			const folderReady = {
				id: folder.id,
				name: folder.name,
				level,
				open,
				checked: chk,
				forHub: otherHub.length ? otherHub[0].for_hub : '',
				otherHub: otherHub.length ? true : false,
			};
			folders.push(folderReady);
		});
		if (insertAt === false) {
			this.arrangedFolders = folders;
		} else {
			// insert the folders at the specified index. using `1` as the second arg deletes the
			/// "Fetching subfolders..." placeholder inserted earlier
			this.arrangedFolders.splice(insertAt, 1, ...folders);
		}
	}

	/**
	 * Detects folders with hooks that are not shown and attempts to show them
	 * @param {String} name - The name/type of the status being reported
	 */
	async showHiddenHookedFolders() {
		const missing = this.theirSavedWebhooks.this_hub.filter(item => {
			return this.arrangedFolders.filter(folder => {
				return folder.id == item.target_id;
			}).length
				? false
				: true;
		});
		if (!missing.length) {
			return;
		}

		const parentFolderResults = await Promise.all(
			missing.map((item, i, missing) => {
				return this.getHiddenHookedParentFolders.call(this, i, missing);
			})
		);
		this.reportStatus('info', this.defStatus);

		for (var i = 0; i < parentFolderResults.length; i++) {
			await this.openHiddenHookedParentFolders(parentFolderResults[i], i, parentFolderResults.length);
		}
	}

	/**
	 * Pulls hidden folder ancestors of a hooked sub folder
	 * @param {Int} idx - The element index of the hooked sub folder
	 * @param {Array} missing - The full hooked-sub-folder array
	 */
	async getHiddenHookedParentFolders(idx, missing) {
		let item = missing[idx];
		this.reportStatus('info', `Looking for synchronized sub folders: ${idx + 1} of ` + missing.length);
		let parentFolders = await this.getParentFolders(item.target_id);
		return parentFolders.reverse();
	}

	/**
	 * Opens hidden folder ancestors of a hooked sub folder
	 * @param {Array} folders - The hooked-sub-folder ancestory
	 * @param {Int} idx - The element index of the ancestory group being processed
	 * @param {Int} len - The length of the all the ancestory groups being processed
	 */
	async openHiddenHookedParentFolders(folders, idx, len) {
		this.reportStatus('info', `Found synchronized sub folders (${idx + 1} of ${len}): ` + folders.length);
		for (let j = 0; j < folders.length; j++) {
			let folderInfo = folders[j];
			for (let k = 0; k < this.arrangedFolders.length; k++) {
				let folder = this.arrangedFolders[k];
				if (folder.id == folderInfo.id) {
					await this.openFolder(folder, k);
					break;
				}
			}
		}
	}

	/**
	 * Assembles all ancestor folders for a given sub folder
	 * @param {Int} childTargetId - The child folder's Box id
	 * @returns {Object[]}
	 */
	async getParentFolders(childTargetId) {
		let parentFolders = [];
		let f = null;
		while ((f = await this.getParentFolder(childTargetId))) {
			childTargetId = f.id;
			if (f.id == '0') {
				break;
			}
			parentFolders.push(f);
		}
		return parentFolders;
	}

	/**
	 * Pulls immediate parent folder for a given sub folder
	 * @param {Int} childTargetId - The child folder's Box id
	 * @throws {String} Will throw an error if the api response is missing required data bits
	 * @returns {Object}
	 */
	async getParentFolder(childTargetId) {
		if (childTargetId == 0) {
			return null;
		}
		const folderInfo = await this.getBoxFolderInfo(childTargetId);
		if (!folderInfo.parent || typeof folderInfo.parent.id == 'undefined') {
			// Parent can be null for folders like root, shared, trash, etc.
			// We'll assume that's what we're looking at and return a top-level object.
			return { id: 0 };
		}
		return folderInfo.parent;
	}

	/**
	 * Logic to handle checking and unchecking folder checkboxes.
	 * Box webhooks have a special rule: If a webhook event is applied to a folder, the webhook will also
	 * fire if the event is triggered on any files/folders under that folder. Because of that checking and unchecking
	 * folders has special logic. Here are the rules:
	 *      1. If a folder is checked, all subfolders are checked.
	 *      2. If a folder is unchecked, all parent/grandparent/etc. folders are unchecked until that folder's root folder.
	 * @param checkedFolderIdx Int  The index (in `this.arrangedFolders`) of the folder whose checkbox was clicked
	 */
	selectOrUnselectFolder(checkedFolderIdx) {
		const checkedFolder = this.arrangedFolders[checkedFolderIdx];
		const arrangedFoldersLength = this.arrangedFolders.length;
		// the folder was checked:
		if (checkedFolder.checked) {
			// if you check a parent, all children should become checked
			for (let i = checkedFolderIdx + 1; i < arrangedFoldersLength; i++) {
				if (!this.arrangedFolders[i] || this.arrangedFolders[i].placeholderItem) {
					continue;
				}
				if (this.arrangedFolders[i].level > checkedFolder.level) {
					this.arrangedFolders[i].checked = true;
				} else {
					break;
				}
			}
		}
		// the folder was unchecked:
		else {
			if (checkedFolderIdx === 0 || checkedFolder.level === 0) {
				// this folder has no parents
				return;
			}
			let parentLevel = checkedFolder.level - 1;
			// If you uncheck a child, the parent should become unchecked
			// starts at the folder higher than the checked folder and removes the check mark if it is a parent folder
			for (let i = checkedFolderIdx - 1; i >= 0; i--) {
				// skip items that are not real folders
				if (this.arrangedFolders[i].placeholderItem) {
					continue;
				}
				const currentFolderLevel = this.arrangedFolders[i].level;
				if (currentFolderLevel < checkedFolder.level && currentFolderLevel === parentLevel) {
					this.arrangedFolders[i].checked = false;
					parentLevel = currentFolderLevel - 1;
				}
				// this is the parent folder of the checked folder
				if (currentFolderLevel === 0) {
					break;
				}
			}
			this.uncheckOtherHubChild(checkedFolderIdx, checkedFolder);
		}
	}

	/**
	 * Unchecks a child folder if it's sync'd to another hub
	 */
	uncheckOtherHubChild(checkedFolderIdx, checkedFolder) {
		const nextIdx = checkedFolderIdx + 1;
		const nextFolder = this.arrangedFolders[nextIdx];
		if (!nextFolder || nextFolder.level <= checkedFolder.level || !nextFolder.otherHub) {
			return;
		}
		this.arrangedFolders[nextIdx].checked = false;
	}

	/**
	 * Gets items in the Box account; filter out any files retrieved.
	 * @param {Int} folderId - The folder's box id
	 * @throws {String} Will throw an error if we get a bad response back from the api and we're not doing a sub-folder fetch
	 * @returns {Object[]}
	 */
	async getBoxFolders(folderId) {
		if (!folderId) {
			this.reportStatus('info', 'Pulling folder list from Box');
		}
		const tmp = await this.Api.request('get', `/v2/box/folders/${folderId}/items`);
		if (this.hasBadResponse(tmp)) {
			const msg = 'Unable to pull Box folder' + (folderId ? '' : 's');
			this.reportStatus('error', msg);
			if (folderId) {
				throw msg;
			} else {
				return [];
			}
		}

		// filter out files
		tmp.data = ensure.array(tmp.data);
		return tmp.data.filter(item => item.type === 'folder');
	}

	/**
	 * Gets Box folder info
	 * @param {Int} folderId - The folder's box id
	 * @throws {String} Will throw an error if we get a bad response back from the api
	 */
	async getBoxFolderInfo(folderId) {
		const tmp = await this.Api.request('get', `/v2/box/folders/${folderId}/info`);
		if (this.hasBadResponse(tmp)) {
			throw 'Unable to pull Box folder information';
		}

		return tmp.data;
	}

	/**
	 * Determine the left padding for folders to make it look like a directory tree
	 * @param folder Object  The folder object in `this.arrangeFolders`
	 * @returns {string}  e.g. '20px'
	 */
	folderPadding(folder) {
		// the `+ 20` part is because table rows have 20px left padding originally
		return folder.level * 20 + 20 + 'px';
	}

	/**
	 * Pulls sub folder data and adds it to the display
	 * @param {Object} folderToOpen - The folder object in `this.arrangeFolders`
	 * @param {Int} openedFolderIndex - The folder object's index in `this.arrangeFolders`
	 * @returns {Boolean} Whether or not the folder was successfully "opened"
	 */
	async openFolder(folderToOpen, openedFolderIndex) {
		if (folderToOpen.placeholderItem || folderToOpen.open === true) {
			return;
		}
		const msg = 'Fetching subfolders';
		this.reportStatus('info', msg);
		folderToOpen.open = true;
		// a placeholder to show user that folders are being fetched
		const loadingPlaceholder = {
			name: `${msg}...`,
			level: folderToOpen.level + 1,
			checked: false,
			placeholderItem: true,
		};
		// placeholder goes in the list just below the "opened" folder
		this.arrangedFolders.splice(openedFolderIndex + 1, 0, loadingPlaceholder);

		try {
			const folders = await this.getBoxFolders(folderToOpen.id);
			if (folders.length === 0) {
				this.arrangedFolders[openedFolderIndex + 1].name = '(empty)';
				return;
			}
			await this.arrangeFolders(folders, openedFolderIndex + 1, folderToOpen.level + 1, false, folderToOpen.checked);
		} catch (err) {
			this.arrangedFolders.splice(openedFolderIndex + 1, 1);
			folderToOpen.open = false;
		}

		return folderToOpen.open;
	}

	/**
	 * Handler for submit action
	 */
	async submitFolders() {
		this.isSaving = true;
		this.isWorking = true;

		try {
			await this.trySubmitFolders();
			this.reportStatus('info', 'All webhooks successfully created.');
			await this.pullFoldersAndHooks();
			this.reportStatus('mismatch', false);
		} catch (err) {
			this.reportStatus('error', err);
		}

		this.reportStatus('info', this.defStatus);
		this.working = false;
		this.isSaving = false;
	}

	/**
	 * Iterates through all folders, finds the top-most checked ones, and submits a request to create webhooks on them
	 * @throws {String} Will throw an error if we get a bad response back from the api (including a 200 response with errors)
	 */
	async trySubmitFolders() {
		const folders = this.arrangedFolders.filter(folder => !folder.placeholderItem);
		const folderCount = folders.length;
		let levelOfCurrentWebhook = false;
		let foldersToWebhook = [];
		for (let i = 0; i < folderCount; i++) {
			const currentFolder = folders[i];
			if (levelOfCurrentWebhook !== false && currentFolder.level > levelOfCurrentWebhook) {
				// this folder is an descendant of a folder that is already getting a webhook
				continue;
			}
			if (currentFolder.checked) {
				levelOfCurrentWebhook = currentFolder.level;
				foldersToWebhook.push({ id: currentFolder.id, name: currentFolder.name });
			} else {
				levelOfCurrentWebhook = false;
			}
		}

		this.reportStatus('info', 'Saving');
		const result = await this.Api.request('post', '/v2/box/webhooks', {
			folders: foldersToWebhook,
		});
		if (this.hasBadResponse(result)) {
			throw 'Unexpected error occurred while saving';
		}

		if (result.data.errors) {
			let msg = '';
			const errs = result.data.errors;
			if (errs.create.length) {
				msg += 'Creating webhooks failed for the following folder(s):<br>';
				msg += '* ' + errs.create.map(err => err.folder_name).join(', ');
			}

			if (errs.delete.length) {
				msg += 'Removing webhooks resulted in the following error(s):<br>';
				msg += errs.delete.join(', ');
			}

			throw msg;
		}
	}

	/**
	 * Confirms whether or not the user wants to perform a connection and data reset
	 */
	confirmReset() {
		let msg = `This will clear the Box connection and start over from stratch.<br><br>
            Are you sure?`;
		this.modal.confirm({ escapedHtml: msg }).then(this.reset.bind(this), () => {});
	}

	/**
	 * Resets connection and hook data for this client
	 */
	async reset() {
		this.setInitialState();
		this.reportStatus('info', 'Resetting');
		const result = await this.Api.request('get', '/v2/box/reset');
		if (this.hasBadResponse(result)) {
			this.reportStatus('error', 'Unexpected error occurred during reset');
			this.working = false;
			return false;
		}
		window.localStorage.removeItem(this.boxStateTokenKey);
		window.localStorage.removeItem(this.boxStateClientKey);
		this.refresh();
	}

	/**
	 * Refreshes the state/page; erasing existing state data if present; in an effort to recover from a persnickety error
	 */
	refresh() {
		this.refreshing = true;
		if (this.hasStateParams()) {
			window.location = '/hub#/admin/integrations/box';
		} else {
			this.$state.reload();
		}
	}

	/**
	 * Checks whether or not we're dealing with state tokens
	 * @return {Boolean}
	 */
	hasStateParams() {
		return this.searchParams.code || this.searchParams.state;
	}

	/**
	 * Confirms whether or not the user wants to perform a refresh
	 */
	confirmRefresh() {
		let msg = 'Refresh page?';
		if (this.hasStateParams()) {
			msg += '<br><strong>Note</strong> this will reset authentication.';
		}
		this.modal.confirm({ escapedHtml: msg }).then(this.refresh.bind(this), () => {});
	}

	/**
	 * Toggles a flag to display a given status box
	 * @param {Object} $event - the js event object
	 * @param {String} name - the name of the status box to toggle
	 */
	toggleStatusBox($event, name) {
		const capName = _.capitalize(name);
		const isShowing = this[`shouldShowStatus${capName}`];
		if (isShowing) {
			this[`shouldShowStatus${capName}`] = false;
			return;
		}

		this.hideStatusBoxes();
		this[`shouldShowStatus${capName}`] = true;
		this.stopProp($event);
	}

	/**
	 * Hides all status boxes
	 */
	hideStatusBoxes() {
		this.shouldShowStatusInfo = false;
		this.shouldShowStatusError = false;
		this.shouldShowStatusMismatch = false;
	}

	/**
	 * Stops event propagation
	 * @param {Object} $event - the js event object
	 */
	stopProp($event) {
		$event.stopPropagation();
	}
}
